// The meetings wire contract: what `/api/meetings*` and `/api/meeting-links*`
// send and the web parses.
//
// Declared once here, for both ends: zod strips an undeclared key, so a field
// the server adds without adding it here never reaches the page.
//
// `MeetingLinkReviewDto` is an ALLOWLIST: the link route builds its answer by
// `MeetingLinkReviewDto.parse(...)`, which drops every key not named here, so
// a transcript, a "Nicht übernommen" row or a forge URL cannot ride along.

import { z } from "zod";

import { IssueEditorPayloadSchema } from "../dashboard/index.js";

import { MEETING_USER_ERROR_CODES } from "./error-codes.js";
import { RUN_ERROR_KINDS } from "./run-state.js";
import {
  MEETING_ANSWER_MAX_CHARS,
  MEETING_ANSWER_VIA,
  MeetingQuestionSchema,
} from "./questions.js";
import {
  ANSWER_EFFECT_FAILURES,
  ANSWER_EFFECT_STATES,
  ANSWER_EFFECTS,
  DOUBT_CODES,
  ITEM_CLASSES,
  ITEM_REASONS,
  MEETING_DOUBT_ANSWERS,
  MEETING_STATUSES,
  RECORDING_FLAGS,
  RECORDING_STARTS,
  REF_KINDS,
  RUN_STEPS,
  STATUS_COMMENT_STATES,
  TICKET_CLASSES,
  TICKET_REASONS,
  TICKET_STATES,
  TOPIC_CARRIES,
  TOPIC_REASONS,
} from "./vocabulary.js";

const isoDate = z.string().min(1);
const iid = z.number().int().positive();

/** `GET /api/meetings/status`: always answers, flag on or off. */
export const MeetingsStatusDto = z.object({
  enabled: z.boolean(),
  /** Absent while the feature is off. */
  googleConnected: z.boolean().optional(),
  /**
   * The connected Google account's e-mail, only when asked for
   * (`?email=1`, the account page's row); `null` when Google gave none.
   */
  googleEmail: z.string().nullable().optional(),
});
export type MeetingsStatusDto = z.infer<typeof MeetingsStatusDto>;

/**
 * `POST /api/meetings` ("Meeting starten"): the meeting Loom made and the Meet
 * link to open. The link may carry `?authuser=<Google e-mail>`.
 */
export const MeetingLaunchDto = z.object({
  meetingId: z.string(),
  meetingUri: z.string().min(1),
});
export type MeetingLaunchDto = z.infer<typeof MeetingLaunchDto>;

/**
 * `POST /api/meetings` body: the project, always (a meeting is started from
 * one project's meetings page), and how recording starts. `manual`, the default, makes the space
 * with auto-recording and auto-transcription OFF, so the host can tell the
 * others first and start both in Meet; `auto` turns both on.
 */
export const MeetingLaunchRequest = z
  .object({
    /** A GitLab id, or a GitHub one (negative), as `MeetingProjectRequest`. */
    projectId: z
      .number()
      .int()
      .refine((n) => n !== 0, "projectId"),
    recordingStart: z.enum(RECORDING_STARTS).default("manual"),
  })
  .strict();
export type MeetingLaunchRequest = z.input<typeof MeetingLaunchRequest>;

/**
 * A meeting recorded without Loom, as the import page lists it
 * (`GET /api/meetings/importable`):
 *
 * - `ready`         every recording is generated; it can be processed now.
 * - `generating`    a recording exists, Google is still writing the file; it
 *                   can be imported, and processing starts once it is done.
 * - `running`       a conference has not ended yet.
 * - `no_recording`  nobody recorded it (only as the answer to a pasted code).
 * - `too_late`      Google deletes the record within 48 hours: no run could
 *                   finish before it is gone.
 * - `imported`      Loom already has it (`meetingId` opens it).
 */
export const MEETING_IMPORT_STATES = [
  "ready",
  "generating",
  "running",
  "no_recording",
  "too_late",
  "imported",
] as const;
export type MeetingImportState = (typeof MEETING_IMPORT_STATES)[number];

/** A Meet conference record's resource name, the only shape the import takes. */
const conferenceName = z.string().regex(/^conferenceRecords\/[A-Za-z0-9_-]+$/u);

/** One meeting (a group of conferences in one space) the owner may import. */
export const MeetingImportableRowDto = z.object({
  /** The newest conference of the group: what the import names. */
  anchorConference: conferenceName,
  conferenceNames: z.array(conferenceName).min(1),
  /** `abc-defg-hij`; null when Google did not say. */
  meetingCode: z.string().nullable(),
  /** The Calendar event's title, matched by the Meet code; null without one. */
  title: z.string().nullable(),
  startedAt: isoDate,
  endedAt: isoDate.nullable(),
  durationS: z.number().int().nonnegative().nullable(),
  /** When Google deletes the record (its `expireTime`, else the end + 30 days). */
  expiresAt: isoDate,
  state: z.enum(MEETING_IMPORT_STATES),
  /** Loom's meeting, for `imported`. */
  meetingId: z.string().nullable(),
});
export type MeetingImportableRowDto = z.infer<typeof MeetingImportableRowDto>;

/** `GET /api/meetings/importable[?code=]`. */
export const MeetingImportableDto = z.object({
  /** The connected Google account (the owner's own, shown to them only). */
  googleEmail: z.string().nullable(),
  rows: z.array(MeetingImportableRowDto),
  /**
   * `none`: a pasted code matched no meeting of the last 30 days that the
   * connected account organized. Absent otherwise.
   */
  reason: z.literal("none").optional(),
});
export type MeetingImportableDto = z.infer<typeof MeetingImportableDto>;

/**
 * `POST /api/meetings/import`: one conference (the anchor) and the project.
 * The server re-reads Google and builds the meeting around the anchor itself;
 * it never takes a grouping from the client. A project is required: an
 * imported meeting is always a project's (owner decision).
 */
export const MeetingImportRequest = z
  .object({
    conferenceName,
    projectId: z
      .number()
      .int()
      .refine((n) => n !== 0, "projectId"),
  })
  .strict();
export type MeetingImportRequest = z.infer<typeof MeetingImportRequest>;

/** `POST /api/meetings/import`: the meeting; `existing` when Loom already had it. */
export const MeetingImportDto = z.object({
  meetingId: z.string(),
  existing: z.boolean(),
});
export type MeetingImportDto = z.infer<typeof MeetingImportDto>;

/** How a filed issue stands now, read from its labels and state (9.4). */
export const MEETING_ISSUE_STATUSES = ["new", "accepted", "declined", "done"] as const;
export type MeetingIssueStatus = (typeof MEETING_ISSUE_STATUSES)[number];

/** A verified quote as Loom shows it to the owner (speaker inside Loom only). */
export const MeetingQuoteDto = z.object({
  text: z.string(),
  approxMs: z.number().int().nonnegative(),
  speakerLabel: z.string().nullable(),
});
export type MeetingQuoteDto = z.infer<typeof MeetingQuoteDto>;

export const MeetingCountsDto = z.object({
  tickets: z.number().int().nonnegative(),
  epics: z.number().int().nonnegative(),
  notTaken: z.number().int().nonnegative(),
  filed: z.number().int().nonnegative(),
  /**
   * "N Punkte warten auf deine Antwort": the items a doubt still holds, the
   * ones past the card's cap included. Optional on the wire so a page loaded
   * before the server sent it still parses.
   */
  waiting: z.number().int().nonnegative().optional(),
});
export type MeetingCountsDto = z.infer<typeof MeetingCountsDto>;

/** One row of `/focus/meetings`. */
export const MeetingListItemDto = z.object({
  meetingId: z.string(),
  spaceId: z.string().nullable(),
  title: z.string().nullable(),
  projectId: z.number().int().nullable(),
  /**
   * The project's name as the forge has it, read under the owner's token;
   * null without a project or when the read failed. Optional on the wire so
   * a page loaded before the server sent it still parses.
   */
  projectName: z.string().nullable().optional(),
  /**
   * How recording starts in the meeting's space: `manual` (the host starts it
   * in Meet) or `auto`. Absent reads as `auto`, what every space before the
   * field was made with.
   */
  recordingStart: z.enum(RECORDING_STARTS).optional(),
  status: z.enum(MEETING_STATUSES),
  statusSince: isoDate,
  /** When Loom created the row; the list is sorted (and grouped) by it. */
  createdAt: isoDate,
  startedAt: isoDate.nullable(),
  endedAt: isoDate.nullable(),
  spontaneous: z.boolean(),
  recordingFlag: z.enum(RECORDING_FLAGS).nullable(),
  /** A closed user-error code when the meeting failed or waits on the owner. */
  failureCode: z.enum(MEETING_USER_ERROR_CODES).nullable(),
  counts: MeetingCountsDto,
});
export type MeetingListItemDto = z.infer<typeof MeetingListItemDto>;

/**
 * Why a ticket row's filing failed, as the owner surface maps it ("Anmeldung
 * nötig", "Kein Zugriff auf das Projekt", "Erneut versuchen").
 * `access_denied` and `request_failed` are not meeting user-error codes, so
 * `failureCode` drops them; this field carries every filing code.
 */
export const MEETING_FILING_FAILURES = [
  "no_token",
  "public_repo",
  "access_denied",
  "content_expired",
  "request_failed",
] as const;
export type MeetingFilingFailure = (typeof MEETING_FILING_FAILURES)[number];

/**
 * One open question of a filed issue (refine light), with its
 * answer when there is one. `answer.via` says who gave it: the owner, or
 * someone holding the magic link ("über den Link beantwortet"). A question
 * the owner closed through "Bearbeiten" is not sent.
 */
export const MeetingQuestionDto = MeetingQuestionSchema.extend({
  answer: z
    .object({
      state: z.enum(["answered", "skipped"]),
      /** The answer's text; null for "Weiss ich nicht". */
      text: z.string().nullable(),
      via: z.enum(MEETING_ANSWER_VIA),
      answeredAt: isoDate,
    })
    .nullable(),
});
export type MeetingQuestionDto = z.infer<typeof MeetingQuestionDto>;

/**
 * `PUT …/issues/:iid/questions/:field` (owner) and
 * `PUT /api/meeting-links/issues/:iid/questions/:field` (link holder): an
 * answer, or "Weiss ich nicht" (`skip`). A list answer is the list control's
 * own text (`Acceptance criteria:` and one `- ` row per line).
 */
export const MeetingAnswerRequest = z.union([
  z.object({ text: z.string().trim().min(1).max(MEETING_ANSWER_MAX_CHARS) }).strict(),
  z.object({ skip: z.literal(true) }).strict(),
]);
export type MeetingAnswerRequest = z.infer<typeof MeetingAnswerRequest>;

/**
 * Whether the issue's `## Aus dem Meeting` block holds what Loom last wanted
 * in it (answers, the open point, link lines): `failed` when the last write
 * did not reach the forge; the next write tries again.
 */
export const MEETING_BODY_SYNC = ["ok", "failed"] as const;
export type MeetingBodySync = (typeof MEETING_BODY_SYNC)[number];

/** One issue filed from the meeting, as the owner sees it. */
export const MeetingIssueRowDto = z.object({
  ticketId: z.string(),
  ordinal: z.number().int().nonnegative(),
  class: z.enum(TICKET_CLASSES),
  reason: z.enum(TICKET_REASONS),
  state: z.enum(TICKET_STATES),
  title: z.string().nullable(),
  issueIid: iid.nullable(),
  issueUrl: z.string().nullable(),
  /** Null until filed, or when the forge read failed. */
  issueStatus: z.enum(MEETING_ISSUE_STATUSES).nullable(),
  quotes: z.array(MeetingQuoteDto),
  /** The draft's question list, before filing only (the body carries it after). */
  openQuestions: z.array(z.string()),
  /**
   * The issue's open questions with their answers, while the issue is still
   * open and needs refinement; none once refine took it over, or once it is
   * declined or done. Optional on the wire so a page loaded before the
   * server sent it still parses.
   */
  questions: z.array(MeetingQuestionDto).optional(),
  /** "Möglicherweise schon #42". */
  existingIid: iid.nullable(),
  failureCode: z.enum(MEETING_USER_ERROR_CODES).nullable(),
  /** The filing's own failure code (`file_failed` rows); absent otherwise. */
  filingFailure: z.enum(MEETING_FILING_FAILURES).nullable().optional(),
  /** "Erneut versuchen" files this row again (`POST /api/meetings/:id/retry`). */
  retryable: z.boolean().optional(),
  /**
   * The issue's description as the forge has it now, so "Bearbeiten" opens
   * with the current text: the edit sends the body trio together, and a form
   * that opened empty would blank it, so a row without it offers no
   * "Bearbeiten". `MeetingLinkIssueDto` has no such field yet: the link
   * holder's edit needs it (a link holder may overwrite that text
   * anyway). Null before filing or when the read failed.
   */
  body: z.string().nullable().optional(),
  /**
   * A row of a reprocess run ("Neu verarbeiten") that is the same subject as
   * an issue this meeting already filed: "Schon angelegt als #N", and no
   * "Anlegen". Null otherwise; optional on the wire so a page loaded before
   * the server sent it still parses.
   */
  filedAs: iid.nullable().optional(),
  /**
   * "Anlegen" files this one row (`POST /api/meetings/:id/tickets/:ticketId/file`):
   * a row of a reprocess run that nothing files on its own, and that is no
   * issue of this meeting yet.
   */
  fileable: z.boolean().optional(),
  /**
   * "Bezug: #N": the open issues the same meeting item reported on (when a
   * status comment is posted there, it names this issue back). Optional on
   * the wire so a page loaded before the server sent it still parses.
   */
  relatedIids: z.array(iid).optional(),
  /**
   * What "Anlegen" would file, before it is filed ("Entwurf ansehen"): the
   * drafted description, outcome and criteria of a `fileable` row; null on
   * every other row. The owner's page only (the link DTO never carries it).
   */
  draftPreview: z
    .object({
      description: z.string(),
      outcome: z.string(),
      acceptanceCriteria: z.array(z.string()),
    })
    .nullable()
    .optional(),
  /**
   * "Ticket aktualisieren" (owner decision 2026-10-19): a `filedAs` row of
   * the current revision whose draft is kept and that has not updated its
   * issue yet. The owner's page only; optional on the wire.
   */
  updatable: z.boolean().optional(),
  /** When "Ticket aktualisieren" rewrote `filedAs`'s body from this row; null before. */
  ticketUpdatedAt: isoDate.nullable().optional(),
  /**
   * How a `filedAs` row (or a straddler hinted at an earlier issue) sits in
   * that issue's scope: the share of the issue's filing lines it holds, and
   * of its lines earlier issues own, the share that are another issue's;
   * `by`: a successor matched by its lines or by its text. Content-free;
   * null on other rows (a pointer row too) and once the run's stage is
   * gone. The owner's page only; optional on the wire.
   */
  successorScope: z
    .object({
      coverage: z.number().min(0).max(1),
      foreignShare: z.number().min(0).max(1),
      by: z.enum(["lines", "text"]).optional(),
    })
    .nullable()
    .optional(),
  /**
   * A doubt still holds this row's item: it was filed as Loom's best guess
   * and the card asks ("Noch offen"). Null once answered or never held.
   * The owner's page only; optional on the wire.
   */
  openDoubt: z.object({ code: z.enum(DOUBT_CODES) }).nullable().optional(),
  /**
   * The issue's links to other issues, as iids: the ones it waits for
   * ("Hängt ab von #N"), the ones waiting for it ("Voraussetzung für #N")
   * and related ones ("Verwandt: #N"). Optional on the wire.
   */
  links: z
    .object({
      dependsOn: z.array(iid),
      neededBy: z.array(iid),
      related: z.array(iid),
    })
    .optional(),
  /**
   * What a doubt's answer does to this filed issue, and how far it got
   * ("Antwort noch nicht im Ticket"); null when no answer touched it.
   * Optional on the wire.
   */
  answerEffect: z
    .object({
      effect: z.enum(ANSWER_EFFECTS),
      state: z.enum(ANSWER_EFFECT_STATES),
      failure: z.enum(ANSWER_EFFECT_FAILURES).nullable(),
    })
    .nullable()
    .optional(),
  /** Whether the issue's meeting block holds Loom's latest lines. Optional on the wire. */
  bodySync: z.enum(MEETING_BODY_SYNC).optional(),
});
export type MeetingIssueRowDto = z.infer<typeof MeetingIssueRowDto>;

/** Whether the ticket was edited since Loom last wrote its body. */
export const TICKET_BODY_EDITED = ["unchanged", "edited", "unknown"] as const;
export type TicketBodyEdited = (typeof TICKET_BODY_EDITED)[number];

/**
 * Why an OPEN ticket reads as already built (owner decision 2026-10-20,
 * "Warn on done/released"): a workflow label that says done or waits for
 * its release (`released_label`, `releasedLabelOf` in `@loom/shared/config`),
 * or a merged merge request that closes or mentions it (`merged_change`).
 */
export const TICKET_DONE_SIGNALS = ["released_label", "merged_change"] as const;
export type TicketDoneSignal = (typeof TICKET_DONE_SIGNALS)[number];

/** Who last changed the description and when, from the forge's own record. */
export const TicketBodyEditDto = z.object({
  /** The editor's display name on the forge (the owner's page only); null
   *  when the forge named nobody. */
  name: z.string().nullable(),
  at: isoDate,
});
export type TicketBodyEditDto = z.infer<typeof TicketBodyEditDto>;

/**
 * Why the update takes a unit out of Loom's own bytes, or would on an edited
 * body (`mergeMeetingUpdate`'s retract): an "Idee:" label on agreed work, a
 * value the judge rejected (another subject's, a contradiction, out of
 * scope), a row another meeting issue already says (`belongs_to`).
 */
export const TICKET_UPDATE_RETRACT_REASONS = [
  "idea_lead",
  "judge_one_subject",
  "judge_no_contradiction",
  "judge_out_of_scope",
  "belongs_to",
] as const;
export type TicketUpdateRetractReason = (typeof TICKET_UPDATE_RETRACT_REASONS)[number];

/**
 * The merge "Ticket aktualisieren" writes (owner 2026-10-30: an update never
 * drops a section): the units of the body as it stands that the new draft
 * does not say and that stay (`kept`), how many units it adds (`added`),
 * what it takes out of Loom's own bytes (`retracted`) and brings back from
 * the filing's draft (`restored`), what it would have taken out of an edited
 * body (`flagged`), the values cut as another meeting issue's (`scopedOut`,
 * "Gehört zu #N"), and the class the new evaluation gives an issue filed as
 * the other one (`classChanged`; its labels stay). The texts are the owner's
 * page only.
 */
export const MeetingTicketUpdateMergeDto = z.object({
  kept: z.array(z.object({ section: z.string(), text: z.string() })),
  added: z.number().int().nonnegative(),
  retracted: z.array(
    z.object({ text: z.string(), reason: z.enum(TICKET_UPDATE_RETRACT_REASONS), iid: iid.optional() }),
  ),
  restored: z.array(z.object({ text: z.string() })),
  flagged: z.array(
    z.object({ text: z.string(), reason: z.enum(TICKET_UPDATE_RETRACT_REASONS), iid: iid.optional() }),
  ),
  scopedOut: z.array(z.object({ iid, count: z.number().int().nonnegative() })),
  classChanged: z.enum(TICKET_CLASSES).optional(),
});
export type MeetingTicketUpdateMergeDto = z.infer<typeof MeetingTicketUpdateMergeDto>;

/**
 * `GET /api/meetings/:id/tickets/:ticketId/update-preview`: the issue's body
 * now next to the whole body "Ticket aktualisieren" would write, and whether
 * the ticket was edited since Loom wrote it (`unknown`: Loom cannot tell, a
 * ticket filed before it kept the body's hash). `same`: the issue already
 * holds the new body (the click only notes the meeting on it).
 * `issueStatus`: how the issue stands now; on a closed one
 * (`done`, or `declined`: a turned-down proposal) the preview warns that the
 * ticket is closed, and the update still rewrites it and leaves it closed
 * (owner decision 2026-10-19). Optional on the wire: a server from before it
 * sends none, and the page then shows no closed warning.
 * `editedBy`: on an `edited` ticket filed before Loom kept the hash, who last
 * changed the description and when (GitLab's "changed the description"
 * system note), so the warning names them. `done`: an open ticket that reads
 * as already built (`TICKET_DONE_SIGNALS`); the page warns and the update
 * stays allowed (owner decision 2026-10-20). Both optional, like
 * `issueStatus`.
 */
export const MeetingTicketUpdatePreviewDto = z.object({
  ticketId: z.string(),
  iid,
  currentBody: z.string(),
  newBody: z.string(),
  currentBodySha256: z.string().regex(/^[0-9a-f]{64}$/u),
  edited: z.enum(TICKET_BODY_EDITED),
  same: z.boolean(),
  updated: z.boolean(),
  issueStatus: z.enum(MEETING_ISSUE_STATUSES).optional(),
  editedBy: TicketBodyEditDto.nullable().optional(),
  done: z.enum(TICKET_DONE_SIGNALS).nullable().optional(),
  /** What the merge did (`MeetingTicketUpdateMergeDto`); optional on the wire. */
  merge: MeetingTicketUpdateMergeDto.optional(),
});
export type MeetingTicketUpdatePreviewDto = z.infer<typeof MeetingTicketUpdatePreviewDto>;

/**
 * `POST /api/meetings/:id/tickets/:ticketId/update`: the hash of the body
 * the preview showed; a body that changed since is refused (409
 * `changed_since_preview`). Answers the row (`MeetingIssueRowDto`).
 */
export const MeetingTicketUpdateRequest = z
  .object({ currentBodySha256: z.string().regex(/^[0-9a-f]{64}$/u) })
  .strict();
export type MeetingTicketUpdateRequest = z.infer<typeof MeetingTicketUpdateRequest>;

/** One "Nicht übernommen" topic. Summary and quotes are null after 30 days. */
export const NotTakenRowDto = z.object({
  topicId: z.string(),
  reason: z.enum(TOPIC_REASONS),
  startMs: z.number().int().nonnegative(),
  summary: z.string().nullable(),
  quotes: z.array(MeetingQuoteDto).nullable(),
  /** The comment on `#iid` for a status update; null for every other topic. */
  statusComment: z
    .object({
      iid,
      state: z.enum(STATUS_COMMENT_STATES),
    })
    .nullable(),
  promotedTicketId: z.string().nullable(),
  /**
   * A status row whose meeting item also became a new issue: that issue's
   * row (`iid` once filed, or "Schon angelegt als #N"; null before). Null
   * on every other row; optional on the wire.
   */
  relatedIssue: z
    .object({ ticketId: z.string(), iid: iid.nullable() })
    .nullable()
    .optional(),
  /**
   * A `doubt` row (an item Loom held): what it would have been
   * and the owner's answer on the doubt card (null while it waits). Null on
   * every other row; optional on the wire so a page loaded before the
   * server sent it still parses.
   */
  held: z
    .object({
      class: z.enum(ITEM_CLASSES).nullable(),
      reason: z.enum(ITEM_REASONS).nullable(),
      answer: z.enum(MEETING_DOUBT_ANSWERS).nullable(),
    })
    .nullable()
    .optional(),
});
export type NotTakenRowDto = z.infer<typeof NotTakenRowDto>;

/** A decision with no work ("Beschlüsse"), owner only, 30 days. */
export const MeetingDecisionDto = z.object({
  topicId: z.string(),
  startMs: z.number().int().nonnegative(),
  summary: z.string().nullable(),
});
export type MeetingDecisionDto = z.infer<typeof MeetingDecisionDto>;

/** At most this many items an answer leaves out (a question holds few). */
const MEETING_DOUBT_LEAVE_OUT_MAX = 50;

/** One item a doubt holds, as the doubt card shows it (owner only). */
export const MeetingDoubtItemDto = z.object({
  topicId: z.string(),
  startMs: z.number().int().nonnegative(),
  /** The item's label; null after 30 days. */
  summary: z.string().nullable(),
  quotes: z.array(MeetingQuoteDto).nullable(),
  /** What Loom would have made of it without the doubt. */
  heldClass: z.enum(ITEM_CLASSES).nullable(),
  heldReason: z.enum(ITEM_REASONS).nullable(),
  /**
   * The issue the item was filed as while the doubt waits ("Angelegt als
   * #N"), its URL and class; null while it is not filed. Optional on the
   * wire so a page loaded before the server sent them still parses.
   */
  filedIid: iid.nullable().optional(),
  filedUrl: z.string().nullable().optional(),
  filedClass: z.enum(TICKET_CLASSES).nullable().optional(),
});
export type MeetingDoubtItemDto = z.infer<typeof MeetingDoubtItemDto>;

/**
 * One question of the doubt card: the items it holds (two for
 * "one topic or two?", every item that points at the same kind of source
 * for a reference outside the transcript, else one), the answers it offers
 * and Loom's own guess. `doubtId` is its first item's topic, the answer
 * route's `:topicId`.
 */
export const MeetingDoubtDto = z.object({
  doubtId: z.string(),
  code: z.enum(DOUBT_CODES),
  /** `unresolved_ref`: where the items point (an earlier meeting, a document); null otherwise. */
  ref: z.enum(REF_KINDS).nullable(),
  items: z.array(MeetingDoubtItemDto).min(1),
  answers: z.array(z.enum(MEETING_DOUBT_ANSWERS)).min(1),
  suggested: z.enum(MEETING_DOUBT_ANSWERS).nullable(),
  /**
   * A question an earlier evaluation asked, carried to this one by its
   * subject ("Neu verarbeiten", `planCarry`): how (`asking`, `asked_again`
   * when the new evaluation raised it too), and the earlier doubt rows' ids.
   * Null and empty for a question of this evaluation alone. Optional on the
   * wire.
   */
  carry: z.enum(TOPIC_CARRIES).nullable().optional(),
  carriedFrom: z.array(z.string()).optional(),
  /**
   * An earlier answer the carry could not apply (its lines did not match
   * both ways): asked again, with that answer shown. Null otherwise.
   */
  earlierAnswer: z.enum(MEETING_DOUBT_ANSWERS).nullable().optional(),
});
export type MeetingDoubtDto = z.infer<typeof MeetingDoubtDto>;

/** The rules that settle a carried "work now or only an idea?" (`settled_<rule>`). */
export const SETTLED_DOUBT_RULES = ["prioritized", "agreed", "committed"] as const;
export type SettledDoubtRule = (typeof SETTLED_DOUBT_RULES)[number];

/**
 * A carried "work now or only an idea?" the new evaluation settled by a
 * named rule (a plain priority, an agreement, our side's commitment on its
 * item): shown on the card as decided ("Im Meeting entschieden"), never
 * asked, no buttons. Owner only.
 */
export const MeetingSettledDoubtDto = z.object({
  topicId: z.string(),
  code: z.enum(DOUBT_CODES),
  rule: z.enum(SETTLED_DOUBT_RULES),
  startMs: z.number().int().nonnegative(),
  /** The item's label; null after 30 days. */
  summary: z.string().nullable(),
  carriedFrom: z.array(z.string()),
});
export type MeetingSettledDoubtDto = z.infer<typeof MeetingSettledDoubtDto>;

/**
 * `POST /api/meetings/:id/doubts/:topicId`: the owner's answer to one
 * question. `leaveOut`: in a question about several items, the items
 * (topic ids) an answer that files each item on its own leaves out; they
 * are not filed. Never all of them, never with "Ein Thema" or "Nichts
 * anlegen".
 */
export const MeetingDoubtAnswerRequest = z
  .object({
    answer: z.enum(MEETING_DOUBT_ANSWERS),
    leaveOut: z.array(z.string().min(1).max(64)).max(MEETING_DOUBT_LEAVE_OUT_MAX).optional(),
  })
  .strict();
export type MeetingDoubtAnswerRequest = z.infer<typeof MeetingDoubtAnswerRequest>;

/**
 * What an answer did: how many issues it filed, and how many failed to;
 * on items already filed, how many issues it changed (`applied`), how many
 * changes still wait to be written (`waiting`) and how many failed
 * (`effectFailed`). The last three are optional on the wire.
 */
export const MeetingDoubtAnswerDto = z.object({
  answer: z.enum(MEETING_DOUBT_ANSWERS),
  filed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  applied: z.number().int().nonnegative().optional(),
  waiting: z.number().int().nonnegative().optional(),
  effectFailed: z.number().int().nonnegative().optional(),
});
export type MeetingDoubtAnswerDto = z.infer<typeof MeetingDoubtAnswerDto>;

/** One line of the meeting's own lists: a decision, a commitment, a question left open. */
export const MeetingNoteDto = z.object({
  key: z.string(),
  /** The line's part about it, cut from the stored line. */
  text: z.string(),
  approxMs: z.number().int().nonnegative(),
  speakerLabel: z.string().nullable(),
  /** The subject it was said about (its label); null when Loom has none. */
  about: z.string().nullable(),
});
export type MeetingNoteDto = z.infer<typeof MeetingNoteDto>;

/**
 * What the meeting decided, what someone said they would do that is no
 * work item (a reply, how the team works) and the questions it left open,
 * beside the issues: on the owner's page only, never written to the
 * project (owner decision 2026-10-16, 3a), and never on the magic link.
 */
export const MeetingListsDto = z.object({
  decisions: z.array(MeetingNoteDto),
  commitments: z.array(MeetingNoteDto),
  questions: z.array(MeetingNoteDto),
});
export type MeetingListsDto = z.infer<typeof MeetingListsDto>;

/**
 * The meeting's magic link as its owner sees it: when it ends,
 * and whether that is past. Never the token: only its SHA-256 is stored, so
 * the token exists once, in the answer that made it.
 */
export const MeetingLinkStateDto = z.object({
  expiresAt: isoDate,
  expired: z.boolean(),
});
export type MeetingLinkStateDto = z.infer<typeof MeetingLinkStateDto>;

/**
 * Where the meeting's run stands, for the owner's page: the step it is at,
 * how many attempts of it failed, why the last one did, and whether one runs
 * now. Content-free: a step name, counts, a closed kind, a flag.
 */
export const MeetingRunStateDto = z.object({
  /** `failed` once the last attempt failed; `failedStep` then names the step. */
  step: z.enum(RUN_STEPS),
  /**
   * The step a failed run failed at (`meeting_run.failed_step`, 0039); null
   * while the run has not failed, and for a run that failed before the step
   * was kept. Absent from a server older than the field.
   */
  failedStep: z.enum(RUN_STEPS).nullable().optional(),
  /** Failed attempts of this step so far; 0 while none failed. */
  attempt: z.number().int().nonnegative(),
  /** How many attempts a step gets before the run fails. */
  maxAttempts: z.number().int().positive(),
  /** Why the last attempt failed; null while none did. */
  error: z.enum(RUN_ERROR_KINDS).nullable(),
  /** A worker holds the run right now (else the next attempt waits its backoff). */
  running: z.boolean(),
  /**
   * The current revision's triage counts (the dedupe stage's numbers:
   * successors, straddlers, overlap links by signal, …), so a run can be
   * checked without the logs. Numbers only; absent once the stage is gone
   * or before the triage wrote it.
   */
  counts: z.record(z.string(), z.number()).optional(),
});
export type MeetingRunStateDto = z.infer<typeof MeetingRunStateDto>;

/** `/focus/meetings/$meetingId`, owner only. */
export const MeetingDetailDto = MeetingListItemDto.extend({
  issues: z.array(MeetingIssueRowDto),
  notTaken: z.array(NotTakenRowDto),
  decisions: z.array(MeetingDecisionDto),
  /** The transcript is still kept (30 days after the meeting ends). */
  transcriptAvailable: z.boolean(),
  /** When the 30-day content goes; null before the meeting has ended. */
  contentExpiresAt: isoDate.nullable(),
  /** The meeting's magic link while one is not revoked; null without one. */
  link: MeetingLinkStateDto.nullable().optional(),
  /**
   * The doubt card: every question that still waits, oldest first, each one
   * answerable; `doubtsPastCap` counts those past `MEETING_MAX_DOUBTS`, which
   * the page folds behind "weitere Rückfragen anzeigen". Optional on the wire
   * so a page loaded before the server sent them still parses.
   */
  doubts: z.array(MeetingDoubtDto).optional(),
  doubtsPastCap: z.number().int().nonnegative().optional(),
  /** Carried questions the new evaluation settled by rule, shown as decided. Optional on the wire. */
  doubtsSettled: z.array(MeetingSettledDoubtDto).optional(),
  /**
   * The meeting's own lists, from its line tags and its transcript; null
   * for a meeting triaged before the line tags, or once its transcript is
   * gone (then `decisions` is what stays).
   */
  lists: MeetingListsDto.nullable().optional(),
  /**
   * The triage's revision: 0 for the run the meeting was processed with, one
   * more for every "Neu verarbeiten". The rows above are the current
   * revision's.
   */
  revision: z.number().int().nonnegative().optional(),
  /**
   * "Neu verarbeiten" may start now: the meeting is `ready` or `failed`, has
   * a project, and its transcript is still kept.
   */
  reprocessable: z.boolean().optional(),
  /**
   * The run, for "Versuch 2 von 4" and "Fehlgeschlagen: …"; null for a
   * meeting with no run. Optional on the wire so a page loaded before the
   * server sent it still parses.
   */
  run: MeetingRunStateDto.nullable().optional(),
  /**
   * "Frühere Auswertung": the rows of earlier revisions, kept and shown
   * read-only (their filed issues stay in the project). Null when the
   * meeting was never processed again.
   */
  superseded: z
    .object({
      issues: z.array(MeetingIssueRowDto),
      notTaken: z.array(NotTakenRowDto),
    })
    .nullable()
    .optional(),
});
export type MeetingDetailDto = z.infer<typeof MeetingDetailDto>;

/**
 * `POST /api/meetings/:id/reprocess` ("Neu verarbeiten"): `started` when this
 * call started a new revision, `running` when one was already running (a
 * second click answers the running one).
 */
export const MeetingReprocessDto = z.object({
  outcome: z.enum(["started", "running"]),
  revision: z.number().int().positive(),
  meeting: MeetingListItemDto,
});
export type MeetingReprocessDto = z.infer<typeof MeetingReprocessDto>;

/**
 * `POST /api/meetings/:id/tickets/file-all` ("Alle neuen anlegen"): how many
 * of the current revision's new rows were filed, failed, or were skipped
 * (being filed by another call, or not due).
 */
export const MeetingFileAllDto = z.object({
  filed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type MeetingFileAllDto = z.infer<typeof MeetingFileAllDto>;

/** `GET /api/meetings`: the owner's meetings, newest first, one page. */
export const MeetingListDto = z.object({
  meetings: z.array(MeetingListItemDto),
  /** The keyset cursor for the next page (`?before=`); null on the last. */
  nextBefore: isoDate.nullable(),
});
export type MeetingListDto = z.infer<typeof MeetingListDto>;

/** One transcript line: the speaker as a Loom label (`S1`, …), never a name. */
export const MeetingTranscriptLineDto = z.object({
  approxMs: z.number().int().nonnegative(),
  speakerLabel: z.string().nullable(),
  text: z.string(),
});
export type MeetingTranscriptLineDto = z.infer<typeof MeetingTranscriptLineDto>;

/** `GET /api/meetings/:id/transcript`, owner only, until `contentExpiresAt`. */
export const MeetingTranscriptDto = z.object({
  meetingId: z.string(),
  contentExpiresAt: isoDate,
  lines: z.array(MeetingTranscriptLineDto),
});
export type MeetingTranscriptDto = z.infer<typeof MeetingTranscriptDto>;

/**
 * `PATCH /api/meetings/:id/issues/:iid` ("Bearbeiten"): the issue form's own
 * fields, each optional (the body trio is sent together or not at all, as on
 * the dashboard), plus the Ticket/Epic switch.
 */
export const MeetingIssuePatchRequest = IssueEditorPayloadSchema.partial()
  .extend({ class: z.enum(TICKET_CLASSES).optional() })
  .strict();
export type MeetingIssuePatchRequest = z.infer<typeof MeetingIssuePatchRequest>;

/** `POST /api/meetings/:id/topics/:topicId/promote` ("Doch übernehmen"). */
export const MeetingPromoteRequest = z.object({ as: z.enum(TICKET_CLASSES) }).strict();
export type MeetingPromoteRequest = z.infer<typeof MeetingPromoteRequest>;

/** `PUT /api/meetings/:id/project`: a GitLab id, or a GitHub one (negative). */
export const MeetingProjectRequest = z
  .object({
    projectId: z
      .number()
      .int()
      .refine((n) => n !== 0, "projectId"),
  })
  .strict();
export type MeetingProjectRequest = z.infer<typeof MeetingProjectRequest>;

/** `POST /api/meetings/:id/retry`: what "Erneut versuchen" started. */
export const MeetingRetryDto = z.object({
  /** `restarted`: a failed run starts again (checkpoints are kept). `filed`:
   *  failed rows and status comments were tried again now. */
  outcome: z.enum(["restarted", "filed"]),
  meeting: MeetingListItemDto,
});
export type MeetingRetryDto = z.infer<typeof MeetingRetryDto>;

/** One issue as a link holder sees it: no forge URL, no failure detail. */
export const MeetingLinkIssueDto = z.object({
  issueIid: iid,
  title: z.string(),
  class: z.enum(TICKET_CLASSES),
  /** Why it became a ticket or an epic ("Zusage", "Anfrage", "Idee", …). */
  reason: z.enum(TICKET_REASONS),
  issueStatus: z.enum(MEETING_ISSUE_STATUSES),
  /** Accept, Edit and Decline are open (the issue is still a proposal). */
  editable: z.boolean(),
  quotes: z.array(MeetingQuoteDto),
  /** Always empty: the issue's questions are `questions`. */
  openQuestions: z.array(z.string()),
  /**
   * The issue's open questions (refine light) with their answers, while it
   * is new or accepted and still needs refinement. Only what the link holder
   * may see: the question, Loom's draft, the anchor (one of the verified
   * quotes above) and the answer.
   */
  questions: z.array(MeetingQuestionDto).optional(),
  existingIid: iid.nullable(),
  /**
   * The issue's description as the forge has it now, so the link holder's
   * "Bearbeiten" opens with the current text (the edit sends the body trio
   * together; a form that opened empty would blank it). Null when the forge
   * sent none, and null once the issue is no longer new: an accepted issue
   * is the team's, and the holder does not read along.
   */
  body: z.string().nullable(),
  /** Whether the issue's meeting block holds the latest answers. Optional on the wire. */
  bodySync: z.enum(MEETING_BODY_SYNC).optional(),
});
export type MeetingLinkIssueDto = z.infer<typeof MeetingLinkIssueDto>;

/** `GET /api/meeting-links/review`: the allowlist (see the header). */
export const MeetingLinkReviewDto = z.object({
  date: isoDate,
  title: z.string().nullable(),
  /** The project's id, for "Verfeinern" (sign-in, then the issue's page). */
  projectId: z.number().int().nullable(),
  projectName: z.string().nullable(),
  issues: z.array(MeetingLinkIssueDto),
});
export type MeetingLinkReviewDto = z.infer<typeof MeetingLinkReviewDto>;

/**
 * `POST /api/meetings/:id/link`: a new link (it replaces a live one). The
 * page builds `/m#k=<token>` from it and shows it once.
 */
export const MeetingLinkCreatedDto = MeetingLinkStateDto.extend({
  token: z.string().min(1),
});
export type MeetingLinkCreatedDto = z.infer<typeof MeetingLinkCreatedDto>;
